//https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/
import React, {
  createElement,
  Fragment,
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react'
import { useSearchAutocomplete } from '@/contexts/SearchAutocompleteContext'
import { createRoot } from 'react-dom/client'
import { autocomplete } from '@algolia/autocomplete-js'
import SearchNoResults from './SearchNoResults'
import SearchAutocompletePlaceholder from './SearchAutocompletePlaceholder'
import SearchAutocompleteLoading from './SearchAutocompleteLoading'
import onItemSelect from './onItemSelect'
import generateAutocompleteFacetSource, {
  TAXONOMY_SEARCH_SOURCE_ID
} from './generateAutocompleteFacetSource'
import locationAutocompleteSource, {
  AUTOCOMPLETE_SOURCE_ID
} from './searchSources/locationAutocompleteSource'
import OverviewMenu from '../OverviewMenu/OverviewMenu'
import setHtmlElementOverflowHidden from '../../utils/setHtmlElementOverflowHidden'
import { SEARCHABLE_FACETS } from '../../domain/term/constants'
import ChipsMenu from '../organisms/ChipsMenu'
import {
  getQueryParamSearchStateLocationName,
  getQueryParamSearchStateLocations,
  getSearchStateLocationName,
  getSearchStateLocations
} from '../../utils/search/searchState'
import { useLocation } from 'react-use'
import { getTitle } from '../../domain/location'
import {
  isBrowsePageType,
  isResourcesPageType,
  isTaxonomyPageType,
  isUtilitiesPageType
} from '../../domain/useCases/pageTypeCheck'
import { UNITED_STATES } from '../../domain/location/constants'
import noop from '../../utils/noop'
import generateAutocompleteCenterSource, {
  CENTER_SEARCH_SOURCE_ID
} from './searchSources/generateAutocompleteCenterSource'
import useSegment from '@/hooks/segment/useSegment'
import useIsMobile from '@/hooks/useIsMobile'
import { Page } from '@/types/models/PageType.model'

export const DEFAULT_PLACEHOLDER_TEXT =
  'Locations, conditions, insurance, centers...'
const SHOW_LOADER_THRESHOLD_TIME = 2000

const KEY_CODE_UP = 'ArrowUp'
const KEY_CODE_DOWN = 'ArrowDown'
const NAVIGATION_KEY_CODES = [KEY_CODE_UP, KEY_CODE_DOWN]
const ALL_LISTS_ID = ''

const SEARCH_STATUS_TO_SHOW_LOADER = ['stalled', 'loading']

const options = [
  {
    title: 'All',
    id: ''
  },
  {
    title: 'Locations',
    id: AUTOCOMPLETE_SOURCE_ID
  },
  {
    title: 'Care Options',
    id: TAXONOMY_SEARCH_SOURCE_ID
  },
  {
    title: 'Providers',
    id: CENTER_SEARCH_SOURCE_ID
  }
]

const getPlaceholderText = ({
  location,
  pageType,
  isGlobalTerm,
  customPlaceholder
}) => {
  if (
    !pageType ||
    isGlobalTerm ||
    isResourcesPageType(pageType) ||
    isUtilitiesPageType(pageType)
  ) {
    return DEFAULT_PLACEHOLDER_TEXT
  }

  if (isTaxonomyPageType(pageType) && !isBrowsePageType(pageType)) {
    return UNITED_STATES
  }

  return (
    getQueryParamSearchStateLocations()?.[0] ||
    getQueryParamSearchStateLocationName()?.split(',')?.[0] ||
    getTitle(location) ||
    getSearchStateLocations()?.[0] ||
    getSearchStateLocationName()?.split(',')?.[0] ||
    customPlaceholder ||
    DEFAULT_PLACEHOLDER_TEXT
  )
}

const placeHolderTextForStaticRendering = ({
  location,
  pageType,
  isGlobalTerm
}) => {
  return isTaxonomyPageType(pageType) &&
    !isBrowsePageType(pageType) &&
    !isResourcesPageType(pageType) &&
    !isUtilitiesPageType(pageType) &&
    !isGlobalTerm
    ? UNITED_STATES
    : getTitle(location)
}

const MOBILE_TOP_NAV_DELAY_MS = 50

const SearchAutocomplete = ({
  id = 'search-autoComplete',
  className = '',
  referralParamValue = 'searchbox',
  location,
  pageType,
  isGlobalTerm
} = {}) => {
  const containerRef = useRef(null)
  const [pressedKey, setPressedKey] = useState(false)
  const [searchAutocompleteInstance, setSearchAutocompleteInstance] =
    useState(null)
  const analytics = useSegment()
  const panelRootRef = useRef(null)
  const rootRef = useRef(null)
  const {
    isOpen: contextIsOpen,
    closeSearch,
    openSearch,
    registerInstance,
    unregisterInstance,
    closeAllOtherInstances,
    getActiveInstanceId
  } = useSearchAutocomplete()

  const browserLocation = useLocation()
  const isMobile = useIsMobile()

  const shouldBeActiveInstance = useCallback(
    (id) => {
      const isHomePage = pageType === Page.HOME

      if (isMobile) {
        if (isHomePage) {
          return id === 'mobile-hero-autocomplete'
        } else {
          return id === 'mobile-top-nav-autocomplete'
        }
      } else {
        if (isHomePage) {
          return id === 'home-autocomplete'
        } else {
          return id === 'desktop-top-nav-autocomplete'
        }
      }
    },
    [isMobile, pageType]
  )

  useEffect(() => {
    setLocationPlaceholder(location, pageType, isGlobalTerm)
  }, [browserLocation, searchAutocompleteInstance])

  useEffect(() => {
    if (searchAutocompleteInstance) {
      if (contextIsOpen) {
        const shouldBeActive = shouldBeActiveInstance(id)
        if (shouldBeActive) {
          closeAllOtherInstances(id)
          searchAutocompleteInstance.setIsOpen(true)
        }
      } else {
        searchAutocompleteInstance.setIsOpen(false)
      }
    }
  }, [
    contextIsOpen,
    searchAutocompleteInstance,
    id,
    getActiveInstanceId,
    shouldBeActiveInstance,
    closeAllOtherInstances
  ])

  const setLocationPlaceholder = (location, pageType, isGlobalTerm) => {
    if (
      getPlaceholderText({ location, pageType, isGlobalTerm }) !==
      DEFAULT_PLACEHOLDER_TEXT
    ) {
      document.getElementById(id).classList.add('location-placeholder')
    } else {
      document.getElementById(id).classList.remove('location-placeholder')
    }
    document
      .getElementById(id)
      .querySelector('.aa-DetachedSearchButtonPlaceholder').innerHTML =
      getPlaceholderText({ location, pageType, isGlobalTerm })
  }

  const detachedOverlayClass = `${id}-detached-overlay`
  const MODAL_INPUT_CLASS = `${id}-input`

  let modalInputElement = null

  const onInputKeyDown = (event) => {
    setPressedKey(event.key)
  }

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    document
      .getElementById(id)
      ?.querySelector('.search-autocomplete-placeholder-element')
      ?.remove()

    const { destroy, setQuery, setIsOpen, setContext, update } = autocomplete({
      container: containerRef.current,
      openOnFocus: true,
      detachedMediaQuery: '',
      classNames: {
        detachedOverlay: detachedOverlayClass,
        input: MODAL_INPUT_CLASS
      },
      initialState: {
        context: {
          activeList: ALL_LISTS_ID,
          increaseLocationsLimit: false
        }
      },
      renderer: { createElement, Fragment, render: noop },
      stallThreshold: SHOW_LOADER_THRESHOLD_TIME,
      placeholder: getPlaceholderText({ location, pageType, isGlobalTerm }),
      getSources() {
        return [
          locationAutocompleteSource({
            onSelect: onItemSelect
          }),
          generateAutocompleteFacetSource({
            indexName: process.env.GATSBY_ALGOLIA_TEST_FEED_LUX,
            sourceId: TAXONOMY_SEARCH_SOURCE_ID,
            headerTitle: 'Care options',
            referralParamValue,
            facetsToSearch: SEARCHABLE_FACETS
          }),
          generateAutocompleteCenterSource({
            indexName: process.env.GATSBY_ALGOLIA_INDEX_NAME,
            brandsIndexName: process.env.GATSBY_ALGOLIA_BRANDS_INDEX_NAME,
            sourceId: CENTER_SEARCH_SOURCE_ID,
            headerTitle: 'Providers',
            analytics
          })
        ]
      },
      renderNoResults({ state, render }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }

        if (!state.query) {
          panelRootRef.current.render(
            <OverviewMenu
              referralParamValue={referralParamValue}
              pageType={pageType}
            />
          )
          return
        }

        if (SEARCH_STATUS_TO_SHOW_LOADER.includes(state.status)) {
          panelRootRef.current.render(
            <SearchAutocompleteLoading className="bg-white" />
          )
          return
        }

        panelRootRef.current.render(
          <div className="flex h-full flex-col">
            <div className="list-container h-full animate-[fadeInList_0.200s_linear] overflow-y-auto">
              <SearchNoResults
                title={`No Results for "${state.query}"`}
                message="Try typing another search term or browse all locations."
                className="bg-white pt-[56px] md:pt-[139px]"
              />
            </div>
          </div>
        )
      },
      render({ children, state, setContext, elements }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }

        const activeList = state.context.activeList

        panelRootRef.current.render(
          <div className="flex h-full flex-col">
            <div
              className="hide-scroll -mx-2 flex shrink-0 overflow-x-auto px-5 md:hidden"
              role="menu"
            >
              <ChipsMenu
                menuClassName="flex gap-2 bg-[rgba(41, 59, 81, 0.5)] [&_button]:first:min-w-[44px] animate-[slideInListSelectionButtonGroup_0.150s_ease-in-out] pb-3"
                optionClassName="active:bg-primary-100 px-2.5 py-1.5 whitespace-nowrap !text-gray-1100 font-medium"
                optionBackgroundClassName="bg-white"
                options={options.map((option) => ({
                  ...option,
                  onClick: ({ id }) => {
                    setContext({ activeList: id })
                  }
                }))}
              />
            </div>

            <div
              className={`list-container h-full animate-[fadeInList_0.200s_linear] overflow-y-auto ${
                activeList !== ALL_LISTS_ID ? 'individual-tab-active' : ''
              }`}
            >
              {/* Stalled status is reached after the time SHOW_LOADER_THRESHOLD_TIME passes */}
              {SEARCH_STATUS_TO_SHOW_LOADER.includes(state.status) ? (
                <SearchAutocompleteLoading className="bg-white" />
              ) : (
                <>
                  {/* Results */}
                  {activeList === ALL_LISTS_ID && children}
                  {activeList === AUTOCOMPLETE_SOURCE_ID &&
                    elements[AUTOCOMPLETE_SOURCE_ID]}
                  {activeList === TAXONOMY_SEARCH_SOURCE_ID &&
                    elements[TAXONOMY_SEARCH_SOURCE_ID]}
                  {activeList === CENTER_SEARCH_SOURCE_ID &&
                    elements[CENTER_SEARCH_SOURCE_ID]}
                </>
              )}
            </div>
          </div>
        )
      },
      onStateChange({ state, prevState, setActiveItemId }) {
        const autocompleteCollection = state.collections.find(
          (collection) => collection.source.sourceId === AUTOCOMPLETE_SOURCE_ID
        )

        const taxonomyCollection = state.collections.find(
          (collection) =>
            collection.source.sourceId === TAXONOMY_SEARCH_SOURCE_ID
        )

        const listToDisplayChanged =
          prevState.context.activeList !== state.context.activeList

        const autocompleteResultItems = autocompleteCollection?.items || []
        const taxonomyCollectionItems = taxonomyCollection?.items || []

        const modalIsOpenAndNoItemSelected =
          state.isOpen &&
          state.activeItemId === null &&
          prevState.activeItemId === null

        const modalIsOpenAndItemWasSelected =
          state.isOpen && state.activeItemId === null
        const modalWasClosedNowOpen = !prevState.isOpen && state.isOpen
        const modalWasOpenNowClosed = prevState.isOpen && !state.isOpen

        // Keyboard navigation
        if (!modalInputElement) {
          modalInputElement =
            document.getElementsByClassName(MODAL_INPUT_CLASS)[0]
        }

        if (modalWasClosedNowOpen) {
          setHtmlElementOverflowHidden(state.isOpen)
          setLocationPlaceholder(location, pageType)
          modalInputElement?.addEventListener('keydown', onInputKeyDown)
          if (!contextIsOpen) {
            openSearch()
          }
        }

        if (modalWasOpenNowClosed) {
          setHtmlElementOverflowHidden(state.isOpen)
          setLocationPlaceholder(location, pageType)
          modalInputElement?.removeEventListener('keydown', onInputKeyDown)
          modalInputElement = null
          // Add delay for mobile TopNav to prevent race conditions
          if (id === 'mobile-top-nav-autocomplete') {
            setTimeout(() => {
              closeSearch()
            }, MOBILE_TOP_NAV_DELAY_MS)
          } else {
            closeSearch()
          }
        }

        if (
          modalIsOpenAndNoItemSelected &&
          autocompleteResultItems.length > 0 &&
          state.query &&
          !NAVIGATION_KEY_CODES.includes(pressedKey)
        ) {
          setActiveItemId(0)
        }

        if (modalIsOpenAndNoItemSelected && pressedKey === KEY_CODE_UP) {
          setActiveItemId(autocompleteResultItems.length - 1)
        }
        if (modalIsOpenAndNoItemSelected && pressedKey === KEY_CODE_DOWN) {
          setActiveItemId(
            state.context.activeList === TAXONOMY_SEARCH_SOURCE_ID
              ? autocompleteResultItems.length
              : 0
          )
        }

        if (modalIsOpenAndItemWasSelected && pressedKey === KEY_CODE_UP) {
          if (state.context.activeList === TAXONOMY_SEARCH_SOURCE_ID) {
            if (prevState.activeItemId === autocompleteResultItems.length - 1) {
              setActiveItemId(
                autocompleteResultItems.length +
                  taxonomyCollectionItems.length -
                  1
              )
            }
          }
        }

        if (listToDisplayChanged) {
          if (
            !state.context.activeList ||
            state.context.activeList === AUTOCOMPLETE_SOURCE_ID
          ) {
            setActiveItemId(0)
          }

          if (state.context.activeList === TAXONOMY_SEARCH_SOURCE_ID) {
            setActiveItemId(autocompleteResultItems.length)
          }
          if (state.context.activeList === CENTER_SEARCH_SOURCE_ID) {
            setActiveItemId(
              [...autocompleteResultItems, ...taxonomyCollectionItems].length
            )
          }
        }

        // Clean search and reset navigation on close
        if (modalWasOpenNowClosed && state.query) {
          setActiveItemId(0)
          setQuery('')
          setIsOpen(false)
        }
      }
    })

    const autocompleteInstance = {
      destroy,
      setQuery,
      setIsOpen,
      setContext,
      update
    }

    // Register this instance
    registerInstance(id, autocompleteInstance)
    setSearchAutocompleteInstance(autocompleteInstance)

    return () => {
      setHtmlElementOverflowHidden(false)
      unregisterInstance(id)
    }
  }, [registerInstance, unregisterInstance])

  return (
    <div
      id={id}
      className={className}
      ref={containerRef}
    >
      <SearchAutocompletePlaceholder
        placeholderText={getPlaceholderText({
          location,
          pageType,
          isGlobalTerm,
          customPlaceholder: placeHolderTextForStaticRendering({
            location,
            pageType,
            isGlobalTerm
          })
        })}
      />
    </div>
  )
}

export default SearchAutocomplete
